/**
 * Returns — wireframe 4 (W2-12): the period list, the gates panel, the
 * lifecycle actions.
 *
 * The gates panel is DRIVEN BY REAL BLOCKERS — the same assemble/tie-out the
 * prepare transition runs — so what the button refuses and what the panel
 * shows are one computation (ADR-0014: the screen renders, it never decides).
 *
 * **Dressed in the legacy's grammar** (ADR-0020, Level 1): navy table head,
 * money cards, banded section rows, a green export control. The dress is
 * inherited; the refusals underneath are ours.
 *
 * The NET OUTWARD / GROSS TOTAL rows the legacy closes on are here — and they
 * arrive **computed**, from `assemble_draft`. This screen renders them and
 * sums nothing (ADR-0014): netting credit notes is a taxable-value decision,
 * and the browser is the wrong place to guess a sign convention.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { FileSpreadsheet, Plus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { useEvaluation } from '../lib/access'
import {
  ApiError,
  type ClientRecord,
  type PeriodGates,
  type PeriodRecord,
  type PeriodTables,
  clients,
  periods,
} from '../lib/api'
import { useClient } from './ClientLayout'

const PERIOD = 'period'

const STATE_LABELS: Record<string, string> = {
  open: 'Open',
  prepared: 'Prepared',
  signed_off: 'Signed off',
  sent_for_approval: 'Out for approval',
  client_approved: 'Client approved',
  filed: 'Filed',
  locked: 'Locked',
}

/** Indian digit grouping — the legacy renders 6,56,755.08, not 656,755.08. */
function rupees(paise: number | undefined): string {
  return ((paise ?? 0) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * The tax of ONE row, from its own components. Within-row arithmetic over
 * figures the service already computed — never a cross-table sum, which is
 * where a screen would start deciding what a return owes.
 */
function rowTax(t: { igst?: number; cgst?: number; sgst?: number } | undefined): number {
  return (t?.igst ?? 0) + (t?.cgst ?? 0) + (t?.sgst ?? 0)
}

const TABLE_LABELS: Record<string, string> = {
  b2b: 'B2B Invoices — 4A, 4B, 4C, 6B, 6C',
  b2cs: 'B2C (Others) — 7',
  cdnr: 'Credit/Debit Notes (Registered) — 9B',
  hsn: 'HSN/SAC Summary — All Outward Supplies',
  nil: 'Nil Rated, Exempted — 8A-8D',
  docs: 'Documents Issued — 13',
}

/**
 * The section tabs, in the legacy's order and wearing its headings.
 *
 * Only sections the draft actually built get a pill — B2CL waits on V-01's
 * unregistered threshold, exports on their V-rows, ECO on V-11. The legacy
 * renders a pill only when the section has rows, so an absent tab reads the
 * same way to an operator who knows it.
 */
const SECTIONS = ['b2b', 'b2cs', 'cdnr', 'hsn', 'nil', 'docs'] as const
type Section = (typeof SECTIONS)[number]

/** Column order per section — the operator reads left to right by habit. */
const COLUMNS: Record<string, Array<{ key: string; label: string; money?: boolean }>> = {
  b2b: [
    { key: 'buyer_name', label: 'Customer Name' },
    { key: 'gstin_buyer', label: 'GSTIN' },
    { key: 'pos', label: 'POS' },
    { key: 'invoice_no', label: 'Inv No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  b2cs: [
    { key: 'pos', label: 'POS' },
    { key: 'rate', label: 'Rate' },
    { key: 'taxable', label: 'Taxable Value', money: true },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  cdnr: [
    { key: 'buyer_name', label: 'Customer Name' },
    { key: 'gstin_buyer', label: 'GSTIN' },
    { key: 'invoice_no', label: 'Note No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  hsn: [
    { key: 'hsn', label: 'HSN/SAC' },
    { key: 'uqc', label: 'UQC' },
    { key: 'qty', label: 'Qty' },
    { key: 'taxable', label: 'Taxable Value', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
}

/** How many rows a section carries — the number on the legacy's pill. */
function sectionCount(tables: PeriodTables | null, key: Section): number {
  const t = tables?.tables?.[key]
  if (Array.isArray(t)) return t.length
  if (t && typeof t === 'object') return Object.keys(t as object).length
  return 0
}

function cell(row: Record<string, unknown>, col: { key: string; money?: boolean }): string {
  const v = row[col.key]
  if (v === null || v === undefined || v === '') return '—'
  if (col.money && typeof v === 'number') return rupees(v)
  return String(v)
}

/**
 * One section of the return, as the legacy's tab shows it: its statutory
 * heading, its own column order, its rows.
 *
 * Rendering only. Every figure was computed by the service; nothing is summed
 * or netted here (ADR-0014). `nil` and `docs` are keyed objects rather than
 * row lists, so they get their own shapes.
 */
function SectionTable({ section, tables }: { section: Section; tables: PeriodTables | null }) {
  const data = tables?.tables?.[section]

  if (section === 'nil' && data && typeof data === 'object') {
    const nil = data as Record<string, number>
    return (
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">{TABLE_LABELS.nil}</th>
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(nil).map(([k, v], i) => (
              <tr key={k} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{k.replaceAll('_', ' ')}</td>
                <td>{rupees(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (section === 'docs' && data && typeof data === 'object') {
    const docs = data as Record<string, Record<string, unknown>>
    return (
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Nature</th>
              <th className="is-text">From</th>
              <th className="is-text">To</th>
              <th>Total</th>
              <th>Distinct</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(docs).map(([nature, d], i) => (
              <tr key={nature} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{nature}</td>
                <td className="is-text">{String(d.from ?? '—')}</td>
                <td className="is-text">{String(d.to ?? '—')}</td>
                <td>{String(d.count ?? 0)}</td>
                <td>{String(d.distinct ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const rows = Array.isArray(data) ? (data as Array<Record<string, unknown>>) : []
  const cols = COLUMNS[section] ?? []
  if (rows.length === 0) return <div className="gst-empty">This section carries no rows.</div>

  return (
    <>
      <p className="gst-sub mb-2">{TABLE_LABELS[section] ?? section.toUpperCase()}</p>
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={c.money ? undefined : 'is-text'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                {cols.map((c, j) => (
                  <td key={c.key} className={c.money ? undefined : 'is-text'}>
                    {j === 0 ? <span className="font-semibold">{cell(r, c)}</span> : cell(r, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="gst-meta mb-4">TOTAL ({rows.length} rows)</p>
    </>
  )
}

export function Periods() {
  const client = useClient()
  const { can } = useEvaluation()

  const [clientRows, setClientRows] = useState<ClientRecord[]>([])
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [gates, setGates] = useState<PeriodGates | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * Sign-off is the one transition that is a judgement rather than a mechanism,
   * so it asks what was checked. Not pre-filled: a confirmation you satisfy by
   * pressing nothing is weaker than one you satisfy by pressing the space bar,
   * and the server refuses that one.
   */
  const [signOffRemark, setSignOffRemark] = useState<string | null>(null)
  const [opening, setOpening] = useState<{ clientId: string; period: string } | null>(null)
  const [ackNo, setAckNo] = useState('')
  const [snapshotFile, setSnapshotFile] = useState<File | null>(null)
  const [snapshotAck, setSnapshotAck] = useState('')
  // The section tabs. `summary` is the legacy's default landing pill.
  const [tab, setTab] = useState<'summary' | Section>('summary')
  const [tables, setTables] = useState<PeriodTables | null>(null)

  /**
   * Returns standing on a person's word alone (FR-9). Derived from the rows we
   * already hold rather than fetched with `filed_unconfirmed=true`: the server
   * offers that filter and the list screen has every row in hand, so a second
   * round trip would only give the two answers a chance to disagree.
   */
  /** The row the rail has selected, when there is one. */
  const selectedRow = rows.find((r) => r.id === selected) ?? null

  const unconfirmed = rows.filter(
    (r) =>
      (r.state === 'filed' || r.state === 'locked') &&
      r.origin !== 'snapshot' &&
      !r.filed_confirmed_at,
  )

  const load = useCallback(async () => {
    try {
      const [clientBody, periodBody] = await Promise.all([
        clients.list({ limit: 200 }),
        // Only this client's periods (ND-C1) — the URL says which.
        periods.list({ client_id: client.id }),
      ])
      setClientRows(clientBody.data)
      setRows(periodBody.data)
      // Not `current ?? …` — see the note in Gstr3b: a retained selection
      // after a client switch shows the previous client's period.
      setSelected(periodBody.data[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.')
    }
  }, [client.id])

  const loadGates = useCallback(async () => {
    if (!selected) {
      setGates(null)
      return
    }
    // A SNAPSHOT HAS NO DRAFT, and that is not a failure. `assemble_draft`
    // refuses it deliberately (ND-3), so asking anyway turned the correct
    // answer into a red error banner reading as though something had broken.
    // Found by opening the screen after importing one.
    if (rows.find((r) => r.id === selected)?.origin === 'snapshot') {
      setGates(null)
      setTables(null)
      setError(null)
      return
    }
    try {
      // Both reads, because the tab strip needs its counts before a tab is
      // opened — the legacy shows the count on the pill, not after the click.
      const [gatesBody, tablesBody] = await Promise.all([
        periods.gates(selected),
        periods.tables(selected),
      ])
      setGates(gatesBody.data)
      setTables(tablesBody.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The period could not be loaded.')
    }
  }, [selected, rows])

  useEffect(() => {
    void load()
  }, [load])
  useEffect(() => {
    void loadGates()
  }, [loadGates])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await Promise.all([load(), loadGates()])
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      } finally {
        setBusy(false)
      }
    },
    [load, loadGates],
  )

  const clientName = (id: string) =>
    clientRows.find((c) => c.id === id)?.legal_name ?? id.slice(0, 8)

  const currentSha = gates?.artifacts.find(
    (a) => a.kind === 'client_workbook' && a.status === 'current',
  )?.sha256

  const actions: Array<{
    label: string
    run: () => Promise<unknown>
    show: boolean
    /** Sign-off asserts something, so it asks before it acts. */
    prompt?: boolean
  }> = gates
    ? [
        {
          label: 'Prepare',
          show: gates.period.state === 'open',
          run: () => periods.transition(gates.period.id, { action: 'prepare' }),
        },
        {
          label: 'Sign off',
          show: gates.period.state === 'prepared',
          prompt: true,
          run: () =>
            periods.transition(gates.period.id, {
              action: 'sign_off',
              remark: signOffRemark ?? '',
            }),
        },
        {
          label: 'Send for approval',
          show: ['prepared', 'signed_off'].includes(gates.period.state),
          run: () => periods.sendForApproval(gates.period.id),
        },
        {
          label: 'Record client approval',
          show: gates.period.state === 'sent_for_approval' && !!currentSha,
          run: () =>
            periods.transition(gates.period.id, {
              action: 'approve',
              artifact_sha256: currentSha,
            }),
        },
        {
          label: 'Lock',
          show: gates.period.state === 'filed',
          run: () => periods.transition(gates.period.id, { action: 'lock' }),
        },
      ]
    : []

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <FileSpreadsheet size={20} /> Returns
          </h1>
          <p className="gst-sub">
            The gates panel and the buttons run the same computation — nothing refuses silently
          </p>
        </div>
        {can(PERIOD, 'can_create') && (
          <Button
            type="button"
            onClick={() => setOpening({ clientId: clientRows[0]?.id ?? '', period: '' })}
            size="sm"
          >
            <Plus size={16} /> Open a period
          </Button>
        )}
      </header>

      {error && (
        <Alert tone="error" className="mb-4">{error}</Alert>
      )}

      {opening && (
        <Card className="mb-6 flex flex-wrap items-end gap-3 p-4">
          <label className="gst-label">
            Client
            <select
              value={opening.clientId}
              onChange={(e) => setOpening({ ...opening, clientId: e.target.value })}
              className="gst-select"
            >
              {clientRows.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legal_name}
                </option>
              ))}
            </select>
          </label>
          <label className="gst-label">
            Period (MM-YYYY)
            <input
              value={opening.period}
              onChange={(e) => setOpening({ ...opening, period: e.target.value })}
              placeholder="08-2026"
              className="gst-input w-28"
            />
          </label>
          <Button
            type="button"
            disabled={!opening.clientId || !/^\d{2}-\d{4}$/.test(opening.period) || busy}
            onClick={() =>
              void act(() =>
                periods.open({
                  client_id: opening.clientId,
                  return_type: 'GSTR1',
                  period: opening.period,
                }),
              ).then(() => setOpening(null))
            }
            size="sm"
          >
            Open GSTR-1
          </Button>
          <Button
            type="button"
            onClick={() => setOpening(null)}
            variant="outline" size="sm"
          >
            Cancel
          </Button>
        </Card>
      )}

      {/*
        * FR-9's list. Returns MARKED filed on somebody's acknowledgment number
        * and never confirmed by the portal's own receipt. It sits above the
        * list rather than inside it because the question is asked of the whole
        * book at once: "which of these do we only have our own word for?"
        * Rendered only when there is something outstanding.
        */}
      {unconfirmed.length > 0 && (
        <Alert tone="warning" className="mb-4">
          <p className="gst-panel-h">
            {unconfirmed.length === 1
              ? 'One return is filed on an acknowledgment number alone'
              : `${unconfirmed.length} returns are filed on an acknowledgment number alone`}
          </p>
          <p className="gst-meta mb-2">
            Upload the portal&rsquo;s filed return against each to confirm it. An ARN is
            what someone typed; the receipt is what the portal said.
          </p>
          <ul className="space-y-1">
            {unconfirmed.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="gst-link"
                  onClick={() => setSelected(row.id)}
                >
                  {row.return_type} · {row.period}
                  {row.filed_ack_no ? ` · ${row.filed_ack_no}` : ''}
                </button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/*
        * X-05. A month filed before ProGST held these books: stored, shown,
        * NEVER recomputed. There is no period to pick — the filed return names
        * its own month, and a caller who names a different one is refused.
        */}
      {can(PERIOD, 'can_create') && (
        <Card className="mb-4 p-3">
          <p className="gst-panel-h">Import a month filed before ProGST</p>
          <p className="gst-meta mb-2">
            The portal&rsquo;s filed return (Returns Dashboard → GSTR-1 → Download Filed
            Return). It is kept as a record of what was filed and is never recomputed
            here, so an amendment has something to point at.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) => setSnapshotFile(e.target.files?.[0] ?? null)}
              className="gst-input"
            />
            <input
              value={snapshotAck}
              onChange={(e) => setSnapshotAck(e.target.value.slice(0, 64))}
              placeholder="Acknowledgment number (optional)"
              className="gst-input w-64"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!snapshotFile || busy}
              onClick={() =>
                void act(async () => {
                  if (!snapshotFile) return
                  await periods.importSnapshot({
                    client_id: client.id,
                    file: snapshotFile,
                    ...(snapshotAck.trim() ? { ack_no: snapshotAck.trim() } : {}),
                  })
                  setSnapshotFile(null)
                  setSnapshotAck('')
                })
              }
            >
              Import as a snapshot
            </Button>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-6">
        {/* -- the period list ---------------------------------------------- */}
        <aside className="w-72 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">{clientName(row.client_id)}</span>
                      <span
                        className={`gst-state ${
                          row.state === 'locked' || row.state === 'filed'
                            ? 'is-go'
                            : row.state === 'open'
                              ? ''
                              : 'is-go'
                        }`}
                      >
                        {STATE_LABELS[row.state] ?? row.state}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px]">
                      {row.return_type} · {row.period}
                      {/*
                        * A snapshot is NOT a period being worked — it is a
                        * record of what was filed before ProGST held these
                        * books, and nothing about it can be recomputed. Saying
                        * so in the rail is what stops someone selecting it and
                        * wondering why every action refuses.
                        */}
                      {row.origin === 'snapshot' && ' · snapshot'}
                      {/*
                        * Marked filed on an acknowledgment number alone (FR-9).
                        * A person's word, not the portal's receipt.
                        */}
                      {(row.state === 'filed' || row.state === 'locked') &&
                        row.origin !== 'snapshot' &&
                        !row.filed_confirmed_at &&
                        ' · unconfirmed'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* -- the detail: gates, totals, actions --------------------------- */}
        <section className="min-w-0 flex-1">
          {selectedRow?.origin === 'snapshot' ? (
            <Card className="p-4">
              <p className="gst-panel-h">
                {selectedRow.period} — a return filed before ProGST held these books
              </p>
              <p className="gst-meta">
                Kept as a record of what the portal says was filed. It is{' '}
                <strong>never recomputed here</strong>, so there are no gates and no
                draft: nothing on this screen can change what this month says.
                {selectedRow.filed_ack_no
                  ? ` Acknowledgment ${selectedRow.filed_ack_no}.`
                  : ''}
              </p>
              <p className="gst-meta mt-2">
                An amendment to a later period can reference it — that is what it is
                here for.
              </p>
            </Card>
          ) : !gates ? (
            <p className="gst-sub">Select a period.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {gates.client_name} — {gates.period.return_type} {gates.period.period}
                  </p>
                  <p className="gst-meta">
                    {STATE_LABELS[gates.period.state] ?? gates.period.state}
                    {gates.period.filed_ack_no && ` · ACK ${gates.period.filed_ack_no}`}
                  </p>
                </div>
                {can(PERIOD, 'can_update') && (
                  <span className="flex flex-wrap gap-2">
                    {actions
                      .filter((a) => a.show)
                      .map((a, i) => (
                        <Button
                          key={a.label}
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            a.prompt ? setSignOffRemark('') : void act(a.run)
                          }
                          variant={i === 0 ? 'primary' : 'outline'} size="sm"
                        >
                          {a.label}
                        </Button>
                      ))}
                    {signOffRemark !== null && (
                      <span className="flex w-full flex-wrap items-center gap-2">
                        <input
                          value={signOffRemark}
                          onChange={(e) =>
                            setSignOffRemark(e.target.value.slice(0, 500))
                          }
                          placeholder="What did you check before signing off?"
                          className="gst-input min-w-72 flex-1"
                        />
                        <Button
                          type="button"
                          disabled={busy || !signOffRemark.trim()}
                          onClick={() =>
                            void act(async () => {
                              const signOff = actions.find((x) => x.prompt)
                              if (signOff) await signOff.run()
                              setSignOffRemark(null)
                            })
                          }
                          size="sm"
                        >
                          Sign off
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setSignOffRemark(null)}
                          variant="outline" size="sm"
                        >
                          Cancel
                        </Button>
                      </span>
                    )}
                    {gates.period.state === 'client_approved' && (
                      <>
                        <input
                          value={ackNo}
                          onChange={(e) => setAckNo(e.target.value)}
                          placeholder="GSTN acknowledgment no"
                          className="gst-input w-44"
                        />
                        <Button
                          type="button"
                          disabled={!ackNo.trim() || busy}
                          onClick={() =>
                            void act(() =>
                              periods.transition(gates.period.id, {
                                action: 'record_filing',
                                ack_no: ackNo.trim(),
                              }),
                            ).then(() => setAckNo(''))
                          }
                          variant="outline" size="sm"
                        >
                          Record filing
                        </Button>
                      </>
                    )}
                  </span>
                )}
              </div>

              {/* the money cards — one table's own figures, never a cross-table sum */}
              <div className="gst-stats mb-4">
                <div className="gst-stat">
                  <div className="n is-money">₹{rupees(gates.totals.b2b?.taxable)}</div>
                  <div className="l">B2B TAXABLE</div>
                </div>
                <div className="gst-stat">
                  <div className="n is-money is-tax">₹{rupees(rowTax(gates.totals.b2b))}</div>
                  <div className="l">B2B TAX</div>
                </div>
                <div className="gst-stat">
                  <div className="n">{gates.blocked_table12.length + gates.tieout.length}</div>
                  <div className="l">BLOCKERS</div>
                </div>
                <div className="gst-stat">
                  <div className="n">{gates.excluded.length}</div>
                  <div className="l">HELD OUT</div>
                </div>
              </div>

              {/* the gates panel — real blockers, verbatim, in the legacy's alert dress */}
              <Alert tone={gates.blocks_prepared ? 'warning' : 'success'} className="mb-4">
                <p className="gst-panel-h">
                  Gates
                  <span className={`gst-tag ${gates.blocks_prepared ? '' : 'is-clear'}`}>
                    {gates.blocks_prepared ? 'blocked · prepare refused' : 'clear'}
                  </span>
                </p>
                {gates.blocked_table12.length === 0 &&
                gates.tieout.length === 0 &&
                gates.pending.length === 0 &&
                gates.excluded.length === 0 ? (
                  <p className="text-sm">Nothing blocks this return. Every reviewed row is in.</p>
                ) : (
                  <ul className="gst-panel-list">
                    {gates.blocked_table12.map((b, i) => (
                      <li key={`b${i}`}>
                        <span className="gst-k">Table 12</span>
                        <span>
                          <code className="gst-c">{String(b.reason)}</code> on row{' '}
                          <code className="gst-c">{String(b.content_key).slice(0, 12)}</code> — blocks
                          prepared
                        </span>
                      </li>
                    ))}
                    {gates.tieout.map((t, i) => (
                      <li key={`t${i}`}>
                        <span className="gst-k">Tie-out</span>
                        <span>{String(t.message)}</span>
                      </li>
                    ))}
                    {gates.excluded.map((e, i) => (
                      <li key={`e${i}`}>
                        <span className="gst-k is-soft">
                          {e.excluded_by === 'reviewer' ? 'Removed' : 'Held out'}
                        </span>
                        <span>
                          row <code className="gst-c">{String(e.content_key).slice(0, 12)}</code>{' '}
                          {/*
                            * A row a PERSON took out has an EMPTY `failing`, so
                            * the old line read "row abc123 () — joins on fix":
                            * empty parentheses, and an instruction to fix
                            * something nobody broke. Exactly the defect the
                            * client workbook carried until 2026-09-09, in a
                            * second surface — found by opening the screen.
                            */}
                          {e.excluded_by === 'reviewer'
                            ? `— removed by the preparer: ${String(
                                e.note || e.reason_code || 'no reason given',
                              )}`
                            : `(${(e.failing as string[]).join(', ')}) — joins on fix`}
                        </span>
                      </li>
                    ))}
                    {gates.pending.map((p, i) => (
                      <li key={`p${i}`}>
                        <span className="gst-k is-soft">Awaiting verification</span>
                        <span>
                          ({String(p.gate)}) row{' '}
                          <code className="gst-c">{String(p.content_key).slice(0, 12)}</code>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>

              {/* the section tabs — the legacy's flow, pill per section with its
                  count, rendered only for sections the draft actually built */}
              <div className="gst-tabs mb-3">
                <button
                  type="button"
                  className="gst-tab"
                  aria-selected={tab === 'summary'}
                  onClick={() => setTab('summary')}
                >
                  Summary
                </button>
                {SECTIONS.filter((k) => sectionCount(tables, k) > 0).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className="gst-tab"
                    aria-selected={tab === k}
                    onClick={() => setTab(k)}
                  >
                    {k.toUpperCase()}
                    <span className="gst-count">{sectionCount(tables, k)}</span>
                  </button>
                ))}
              </div>

              {tab !== 'summary' && (
                <SectionTable section={tab} tables={tables} />
              )}

              {/* the return, in numbers — the legacy's section-banded summary */}
              {tab === 'summary' && (
              <div className="gst-table-wrap mb-4">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Section</th>
                      <th className="is-text">Table</th>
                      <th>Taxable (₹)</th>
                      <th>IGST (₹)</th>
                      <th>CGST (₹)</th>
                      <th>SGST (₹)</th>
                      <th>Tax (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['b2b', 'b2cs', 'cdnr'] as const).map((table, i) => (
                      <tr key={table} className={i % 2 === 0 ? 'is-a' : ''}>
                        <td className="is-sec">{table.toUpperCase()}</td>
                        <td className="is-text">{TABLE_LABELS[table]}</td>
                        <td>{rupees(gates.totals[table]?.taxable)}</td>
                        <td>{rupees(gates.totals[table]?.igst)}</td>
                        <td>{rupees(gates.totals[table]?.cgst)}</td>
                        <td>{rupees(gates.totals[table]?.sgst)}</td>
                        <td>{rupees(rowTax(gates.totals[table]))}</td>
                      </tr>
                    ))}
                    {/* The legacy's closing pair, computed service-side. */}
                    <tr className="is-net">
                      <td className="is-sec">NET OUTWARD</td>
                      <td className="is-text">(excl. NIL/Exempt)</td>
                      <td>{rupees(gates.totals.net_outward?.taxable)}</td>
                      <td>{rupees(gates.totals.net_outward?.igst)}</td>
                      <td>{rupees(gates.totals.net_outward?.cgst)}</td>
                      <td>{rupees(gates.totals.net_outward?.sgst)}</td>
                      <td>{rupees(rowTax(gates.totals.net_outward))}</td>
                    </tr>
                    <tr className="is-gross">
                      <td className="is-sec">GROSS TOTAL</td>
                      <td className="is-text">(incl. NIL/Exempt/Non-GST)</td>
                      <td>{rupees(gates.totals.gross_total?.taxable)}</td>
                      <td>{rupees(gates.totals.gross_total?.igst)}</td>
                      <td>{rupees(gates.totals.gross_total?.cgst)}</td>
                      <td>{rupees(gates.totals.gross_total?.sgst)}</td>
                      <td>{rupees(rowTax(gates.totals.gross_total))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}

              {gates.artifacts.length > 0 && (
                <Card className="p-3">
                  {gates.artifacts.map((a) => (
                    <p key={`${a.kind}v${a.version_no}`} className="gst-meta">
                      {a.kind.replaceAll('_', ' ')} v{a.version_no} · {a.status} ·{' '}
                      <span className="gst-mono">{a.sha256.slice(0, 16)}…</span> · from{' '}
                      {a.generated_from_state}
                    </p>
                  ))}
                </Card>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
